import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const currentYear = new Date().getFullYear();

const monthOptions = Array.from({ length: 12 }, (_, idx) => idx + 1);

const formatNumber = (value) => {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return '-';
  return num.toLocaleString('ko-KR');
};

const aggregateInsuranceField = (rows, fieldKey) => {
  const sum = rows.reduce((acc, row) => acc + Number(row[fieldKey] || 0), 0);
  const count = rows.filter((row) => Number(row[fieldKey] || 0) !== 0).length;
  return { count, sum };
};

const tableContainer = {
  marginTop: '20px',
  border: '1px solid #e0e0e0',
  borderRadius: '8px',
  overflow: 'hidden',
};

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const thStyle = {
  background: '#f2f4f6',
  borderBottom: '1px solid #d0d7de',
  padding: '10px',
  textAlign: 'center',
  fontWeight: 600,
};

const tdStyle = {
  borderBottom: '1px solid #eef2f6',
  padding: '10px',
  textAlign: 'center',
};

const filterGroup = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  flexWrap: 'wrap',
  marginBottom: '16px',
};

const labelStyle = { fontWeight: 600 };
const inputStyle = {
  padding: '8px 10px',
  borderRadius: '6px',
  border: '1px solid #d0d7de',
};

const buttonStyle = {
  padding: '10px 16px',
  background: '#2563eb',
  color: '#fff',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
};

const secondaryButton = {
  ...buttonStyle,
  background: '#475569',
};

const infoBox = {
  marginTop: '16px',
  padding: '12px 16px',
  background: '#f8fafc',
  border: '1px solid #cbd5f5',
  borderRadius: '8px',
  fontSize: '0.9rem',
  lineHeight: 1.5,
};

const SORT_KEYS = {
  participantYear: 'participantYear',
  name: 'name',
  employmentIndividual: 'employmentIndividual',
  employmentInstitution: 'employmentInstitution',
  healthIndividual: 'healthIndividual',
  healthInstitution: 'healthInstitution',
  longTermCareIndividual: 'longTermCareIndividual',
  longTermCareInstitution: 'longTermCareInstitution',
  industrialInstitution: 'industrialInstitution',
};

/** 목록 표시용: 고용·건강·요양·산재 전 항목이 0원이면 숨김(음수·양수는 값 있음) */
const INSURANCE_AMOUNT_FIELD_KEYS = [
  'employmentIndividual',
  'employmentInstitution',
  'healthIndividual',
  'healthInstitution',
  'longTermCareIndividual',
  'longTermCareInstitution',
  'industrialInstitution',
];

const rowHasAnyInsuranceAmount = (row) =>
  INSURANCE_AMOUNT_FIELD_KEYS.some((key) => {
    const n = Number(row[key]);
    return Number.isFinite(n) && n !== 0;
  });

const MISMATCH_REASON_PREFIX = '당해/전년도 전체 명단에 없습니다';

/** errors 배열에서 명단 불일치 행만 추려 이름 리스트로 반환 */
const extractMismatchNames = (errors) => {
  if (!Array.isArray(errors)) return [];
  const names = [];
  const seen = new Set();
  errors.forEach((e) => {
    if (!e || typeof e.reason !== 'string') return;
    if (!e.reason.startsWith(MISMATCH_REASON_PREFIX)) return;
    const name = (e.row && e.row.name) ? String(e.row.name).trim() : '';
    if (!name) return;
    if (seen.has(name)) return;
    seen.add(name);
    names.push(name);
  });
  return names;
};

/** errors 배열에서 성명·생년월일 형식 오류 행만 추려 이름 리스트로 반환 */
const extractFormatErrorNames = (errors) => {
  if (!Array.isArray(errors)) return [];
  const names = [];
  const seen = new Set();
  errors.forEach((e) => {
    if (!e || typeof e.reason !== 'string') return;
    if (e.reason.startsWith(MISMATCH_REASON_PREFIX)) return;
    const name = (e.row && e.row.name) ? String(e.row.name).trim() : '(성명 없음)';
    const key = `${name}__${e.reason}`;
    if (seen.has(key)) return;
    seen.add(key);
    names.push(name);
  });
  return names;
};

/** 고용·건강·산재 엑셀 반영 결과 문구(동일 형식) */
const buildInsuranceImportSuccessMessage = (data) => {
  const imp = Number(data.importedCount || 0);
  const u60 = Number(data.under60ExcludedCount || 0);
  const mm = Number(data.mismatchCount || 0);
  const fmtErr = Number(data.errorCount || 0) - mm;
  const parts = [
    `${imp}건 반영`,
    `만 60세 미만 ${u60}건 미반영`,
    `명단 불일치 ${mm}건 미반영`,
  ];
  if (fmtErr > 0) {
    parts.push(`성명·생년월일 오류 ${fmtErr}건`);
  }
  let message = parts.join(' · ');

  const mismatchNames = extractMismatchNames(data.errors);
  if (mismatchNames.length > 0) {
    message += `\n\n▶ 명단 불일치(전산 명단에 없음): ${mismatchNames.join(', ')}\n   해당 인원은 참여자 등록을 먼저 확인해 주세요.`;
  }

  const formatErrorNames = extractFormatErrorNames(data.errors);
  if (formatErrorNames.length > 0) {
    message += `\n\n▶ 성명·생년월일 형식 오류: ${formatErrorNames.join(', ')}`;
  }

  return message;
};

/** 엑셀 업로드 진행 중 오버레이: 고용 / 건강·요양 / 산재 구분 */
const IMPORT_UPLOAD_COPY = {
  employment: {
    title: '고용보험 엑셀 등록 중',
    body: '서버에 파일을 보내고 고용보험(본인·기관) 금액을 반영하고 있습니다.',
    ariaLabel: '고용보험 엑셀 등록 진행 중',
  },
  health: {
    title: '건강·요양보험 엑셀 등록 중',
    body: '서버에 파일을 보내고 건강보험·요양보험(본인·기관) 금액을 반영하고 있습니다.',
    ariaLabel: '건강·요양보험 엑셀 등록 진행 중',
  },
  industrial: {
    title: '산재보험 엑셀 등록 중',
    body: '서버에 파일을 보내고 산재보험(기관) 금액을 반영하고 있습니다.',
    ariaLabel: '산재보험 엑셀 등록 진행 중',
  },
};

const MONTH_REQUIRED_MODAL = {
  type: 'info',
  title: '월을 선택해 주세요',
  message:
    '조회·엑셀 불러오기·입력 초기화는 월을 선택한 뒤에 진행할 수 있습니다.\n연도는 당해연도가 기본이며, 월은 목록에서 선택해 주세요.',
};

function EmploymentInsurancePage() {
  const { user } = useAuth();
  const [year, setYear] = useState(currentYear);
  const [month, setMonth] = useState(null);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [nameKeyword, setNameKeyword] = useState('');
  const [payrolls, setPayrolls] = useState([]);
  const [loading, setLoading] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelPassword, setExcelPassword] = useState('');
  const [resultMessage, setResultMessage] = useState(null);
  const [errors, setErrors] = useState([]);
  const [resetModalPhase, setResetModalPhase] = useState(null);
  const [resetLoading, setResetLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);
  /** null이 아니면 해당 종류 엑셀 업로드 진행 중 */
  const [importUploadKind, setImportUploadKind] = useState(null);
  const [feedbackModal, setFeedbackModal] = useState(null);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  /** 참여연도 정렬(헤더 클릭 전 기본): 당해 우선 → 성명 */
  const [participantYearSortPreset, setParticipantYearSortPreset] = useState('current_first');

  const excelFileInputRef = useRef(null);
  const clearExcelFileSelection = useCallback(() => {
    setExcelFile(null);
    if (excelFileInputRef.current) {
      excelFileInputRef.current.value = '';
    }
  }, []);

  const fetchProjects = async (targetYear = year) => {
    try {
      const response = await api.get('/projects', { params: { year: targetYear } });
      setProjects(response.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchPayrolls = async () => {
    if (month == null || month < 1 || month > 12) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.get('/insurance/employment', {
        params: {
          year,
          month,
          projectId: projectId || undefined,
          name: nameKeyword || undefined,
        },
      });
      setPayrolls(response.data || []);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(year);
  }, [year]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleSearch = () => {
    if (month == null || month < 1 || month > 12) {
      setFeedbackModal(MONTH_REQUIRED_MODAL);
      return;
    }
    fetchPayrolls();
  };

  const displayedPayrolls = useMemo(
    () => payrolls.filter((row) => rowHasAnyInsuranceAmount(row)),
    [payrolls]
  );

  const sortedPayrolls = useMemo(() => {
    const list = [...displayedPayrolls];
    const numField = (v) => {
      const n = Number(v || 0);
      return Number.isFinite(n) ? n : 0;
    };
    const numYear = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const nameCompare = (a, b) => {
      const na = a.Participant?.name || '';
      const nb = b.Participant?.name || '';
      return na.localeCompare(nb, 'ko');
    };

    const compareByYearPreset = (a, b) => {
      const pyA = numYear(a.participantYear);
      const pyB = numYear(b.participantYear);
      let cmp = 0;
      if (participantYearSortPreset === 'previous_first') {
        const tier = (py) => {
          if (py === null) return 999;
          if (py === year - 1) return 0;
          if (py === year) return 1;
          return 2 + Math.min(Math.abs(py - (year - 1)), 100);
        };
        cmp = tier(pyA) - tier(pyB);
      } else if (participantYearSortPreset === 'year_asc') {
        cmp = (pyA !== null ? pyA : 99999) - (pyB !== null ? pyB : 99999);
      } else if (participantYearSortPreset === 'year_desc') {
        cmp = (pyB !== null ? pyB : -99999) - (pyA !== null ? pyA : -99999);
      } else {
        const tier = (py) => {
          if (py === null) return 999;
          if (py === year) return 0;
          if (py === year - 1) return 1;
          return 2 + Math.min(Math.abs(py - year), 100);
        };
        cmp = tier(pyA) - tier(pyB);
      }
      if (cmp !== 0) return cmp;
      return nameCompare(a, b);
    };

    if (!sortKey) {
      return list.sort(compareByYearPreset);
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    return list.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case SORT_KEYS.participantYear: {
          const pa = numYear(a.participantYear);
          const pb = numYear(b.participantYear);
          cmp = (pa !== null ? pa : 0) - (pb !== null ? pb : 0);
          break;
        }
        case SORT_KEYS.name: {
          const na = a.Participant?.name || '';
          const nb = b.Participant?.name || '';
          cmp = na.localeCompare(nb, 'ko');
          break;
        }
        case SORT_KEYS.employmentIndividual:
          cmp = numField(a.employmentIndividual) - numField(b.employmentIndividual);
          break;
        case SORT_KEYS.employmentInstitution:
          cmp = numField(a.employmentInstitution) - numField(b.employmentInstitution);
          break;
        case SORT_KEYS.healthIndividual:
          cmp = numField(a.healthIndividual) - numField(b.healthIndividual);
          break;
        case SORT_KEYS.healthInstitution:
          cmp = numField(a.healthInstitution) - numField(b.healthInstitution);
          break;
        case SORT_KEYS.longTermCareIndividual:
          cmp = numField(a.longTermCareIndividual) - numField(b.longTermCareIndividual);
          break;
        case SORT_KEYS.longTermCareInstitution:
          cmp = numField(a.longTermCareInstitution) - numField(b.longTermCareInstitution);
          break;
        case SORT_KEYS.industrialInstitution:
          cmp = numField(a.industrialInstitution) - numField(b.industrialInstitution);
          break;
        default:
          return 0;
      }
      if (cmp !== 0) return cmp * dir;
      const nc = nameCompare(a, b);
      if (nc !== 0) return nc;
      return (a.participantId || 0) - (b.participantId || 0);
    });
  }, [displayedPayrolls, sortKey, sortDir, year, participantYearSortPreset]);

  const handleSortHeader = (key) => {
    if (sortKey !== key) {
      setSortKey(key);
      setSortDir('asc');
    } else {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    }
  };

  const sortArrow = (key) => {
    if (sortKey !== key) return null;
    return (
      <span style={{ marginLeft: 4, fontSize: '0.85em', color: '#2563eb' }} aria-hidden>
        {sortDir === 'asc' ? '↑' : '↓'}
      </span>
    );
  };

  const handleExcelUpload = async () => {
    if (month == null || month < 1 || month > 12) {
      setFeedbackModal(MONTH_REQUIRED_MODAL);
      return;
    }
    if (!excelFile) {
      const text = '엑셀 파일을 선택해주세요.';
      setResultMessage({ type: 'error', text });
      setFeedbackModal({ type: 'error', message: text });
      return;
    }
    const formData = new FormData();
    formData.append('excelFile', excelFile);
    formData.append('year', year);
    formData.append('month', month);
    if (excelPassword) {
      formData.append('password', excelPassword);
    }
    const confirmed = window.confirm(
      `${year}년 ${month}월 고용보험 값을 일괄 등록합니다.\n해당 월 자료가 맞는지 재확인 후 불러오기를 진행해 주세요.`
    );
    if (!confirmed) {
      return;
    }

    setImportUploadKind('employment');
    try {
      const response = await api.post('/insurance/employment/import', formData);
      const successText = `고용보험 · ${buildInsuranceImportSuccessMessage(response.data)}`;
      setResultMessage({ type: 'success', text: successText });
      setErrors(response.data.errors || []);
      setFeedbackModal({ type: 'success', message: successText, title: '고용보험 반영 완료' });
      fetchPayrolls();
    } catch (error) {
      const message = error.response?.data?.message || '업로드 중 오류가 발생했습니다.';
      setResultMessage({ type: 'error', text: message });
      setFeedbackModal({ type: 'error', message, title: '고용보험 엑셀 오류' });
    } finally {
      setImportUploadKind(null);
      setExcelPassword('');
      clearExcelFileSelection();
    }
  };

  const handleHealthExcelUpload = async () => {
    if (month == null || month < 1 || month > 12) {
      setFeedbackModal(MONTH_REQUIRED_MODAL);
      return;
    }
    if (!excelFile) {
      const text = '엑셀 파일을 선택해주세요.';
      setResultMessage({ type: 'error', text });
      setFeedbackModal({ type: 'error', message: text });
      return;
    }
    const formData = new FormData();
    formData.append('excelFile', excelFile);
    formData.append('year', year);
    formData.append('month', month);
    if (excelPassword) {
      formData.append('password', excelPassword);
    }
    const confirmed = window.confirm(
      `${year}년 ${month}월 건강보험/요양보험 값을 일괄 등록합니다.\n해당 월 자료가 맞는지 재확인 후 불러오기를 진행해 주세요.`
    );
    if (!confirmed) {
      return;
    }

    setImportUploadKind('health');
    try {
      const response = await api.post('/insurance/health/import', formData);
      const successText = `건강·요양보험 · ${buildInsuranceImportSuccessMessage(response.data)}`;
      setResultMessage({ type: 'success', text: successText });
      setErrors(response.data.errors || []);
      setFeedbackModal({ type: 'success', message: successText, title: '건강·요양보험 반영 완료' });
      fetchPayrolls();
    } catch (error) {
      const message = error.response?.data?.message || '업로드 중 오류가 발생했습니다.';
      setResultMessage({ type: 'error', text: message });
      setFeedbackModal({ type: 'error', message, title: '건강·요양보험 엑셀 오류' });
    } finally {
      setImportUploadKind(null);
      setExcelPassword('');
      clearExcelFileSelection();
    }
  };

  const handleIndustrialExcelUpload = async () => {
    if (month == null || month < 1 || month > 12) {
      setFeedbackModal(MONTH_REQUIRED_MODAL);
      return;
    }
    if (!excelFile) {
      const text = '엑셀 파일을 선택해주세요.';
      setResultMessage({ type: 'error', text });
      setFeedbackModal({ type: 'error', message: text });
      return;
    }
    const formData = new FormData();
    formData.append('excelFile', excelFile);
    formData.append('year', year);
    formData.append('month', month);
    if (excelPassword) {
      formData.append('password', excelPassword);
    }
    const confirmed = window.confirm(
      `${year}년 ${month}월 산재보험 값을 일괄 등록합니다.\n해당 월 자료가 맞는지 재확인 후 불러오기를 진행해 주세요.`
    );
    if (!confirmed) {
      return;
    }

    setImportUploadKind('industrial');
    try {
      const response = await api.post('/insurance/industrial/import', formData);
      const successText = `산재보험 · ${buildInsuranceImportSuccessMessage(response.data)}`;
      setResultMessage({ type: 'success', text: successText });
      setErrors(response.data.errors || []);
      setFeedbackModal({ type: 'success', message: successText, title: '산재보험 반영 완료' });
      fetchPayrolls();
    } catch (error) {
      const message = error.response?.data?.message || '업로드 중 오류가 발생했습니다.';
      setResultMessage({ type: 'error', text: message });
      setFeedbackModal({ type: 'error', message, title: '산재보험 엑셀 오류' });
    } finally {
      setImportUploadKind(null);
      setExcelPassword('');
      clearExcelFileSelection();
    }
  };

  const selectedProjectName =
    projectId && String(projectId).trim() !== ''
      ? projects.find((p) => String(p.id) === String(projectId))?.projectName || '선택 사업단'
      : null;

  const handleResetInsuranceExecute = async () => {
    if (month == null || month < 1 || month > 12) {
      setFeedbackModal(MONTH_REQUIRED_MODAL);
      setResetModalPhase(null);
      return;
    }
    setResetLoading(true);
    try {
      const payload = { year, month };
      if (projectId && String(projectId).trim() !== '') {
        payload.projectId = Number(projectId);
      }
      const response = await api.post('/insurance/month/reset', payload);
      const n = Number(response.data?.deleted ?? 0);
      const scope = selectedProjectName ? `${selectedProjectName} · ` : '';
      const text = `${year}년 ${month}월 · ${scope}저장된 사회보험 입력 ${n}건을 삭제했습니다.`;
      setResultMessage({ type: 'success', text });
      setFeedbackModal({ type: 'success', message: text });
      setResetModalPhase(null);
      fetchPayrolls();
    } catch (error) {
      const message = error.response?.data?.message || '초기화 중 오류가 발생했습니다.';
      setResultMessage({ type: 'error', text: message });
      setFeedbackModal({ type: 'error', message });
      setResetModalPhase(null);
    } finally {
      setResetLoading(false);
    }
  };

  const listedParticipantCount = displayedPayrolls.length;
  const eiInd = aggregateInsuranceField(displayedPayrolls, 'employmentIndividual');
  const eiIns = aggregateInsuranceField(displayedPayrolls, 'employmentInstitution');
  const hiInd = aggregateInsuranceField(displayedPayrolls, 'healthIndividual');
  const hiIns = aggregateInsuranceField(displayedPayrolls, 'healthInstitution');
  const ltcInd = aggregateInsuranceField(displayedPayrolls, 'longTermCareIndividual');
  const ltcIns = aggregateInsuranceField(displayedPayrolls, 'longTermCareInstitution');
  const indIns = aggregateInsuranceField(displayedPayrolls, 'industrialInstitution');

  const stickyBarStyle = {
    position: isMobile ? 'static' : 'sticky',
    top: isMobile ? 'auto' : 60,
    zIndex: isMobile ? 'auto' : 5,
    background: '#ffffff',
    paddingBottom: 8,
    borderBottom: '1px solid #e5e7eb',
  };

  const colCount = 10;

  const sortableThBase = {
    cursor: 'pointer',
    userSelect: 'none',
  };

  const renderSortableTh = (label, key, styleExtra = {}) => (
    <th
      style={{ ...thStyle, ...sortableThBase, ...styleExtra }}
      onClick={() => handleSortHeader(key)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSortHeader(key);
        }
      }}
      tabIndex={0}
      role="columnheader"
      aria-sort={
        sortKey === key ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'
      }
    >
      {label}
      {sortArrow(key)}
    </th>
  );

  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }

  return (
    <div>
      <div style={stickyBarStyle}>
        <h2 style={{ marginBottom: '6px' }}>사회보험 입력</h2>
        <div style={{ fontSize: '0.9rem', color: '#64748b', marginBottom: '10px' }}>
          4대보험 데이터를 월 기준으로 순차 업로드하여 반영합니다.
        </div>

        <div style={filterGroup}>
          <label style={labelStyle}>
            연도
            <input
              type="number"
              value={year}
              min="2000"
              style={{ ...inputStyle, marginLeft: '6px', width: '110px' }}
              onChange={(e) => setYear(Number(e.target.value))}
            />
          </label>

          <label style={labelStyle}>
            월
            <select
              value={month ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                setMonth(v === '' ? null : Number(v));
              }}
              style={{ ...inputStyle, marginLeft: '6px' }}
            >
              <option value="">월 선택</option>
              {monthOptions.map((m) => (
                <option key={m} value={m}>
                  {m}월
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            사업단
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              style={{ ...inputStyle, marginLeft: '6px' }}
            >
              <option value="">전체</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.projectName}
                </option>
              ))}
            </select>
          </label>

          <label style={labelStyle}>
            성명
            <input
              type="text"
              value={nameKeyword}
              style={{ ...inputStyle, marginLeft: '6px' }}
              placeholder="성명 검색"
              onChange={(e) => setNameKeyword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleSearch();
                }
              }}
            />
          </label>

          <button style={secondaryButton} onClick={handleSearch} disabled={loading}>
            조회
          </button>
        </div>

        <div style={{ ...filterGroup, marginTop: '4px', marginBottom: '4px' }}>
          <label style={labelStyle}>
            참여연도 정렬
            <select
              value={participantYearSortPreset}
              onChange={(e) => setParticipantYearSortPreset(e.target.value)}
              style={{ ...inputStyle, marginLeft: '6px', minWidth: '220px' }}
            >
              <option value="current_first">당해 우선 → 성명</option>
              <option value="previous_first">전년 우선 → 성명</option>
              <option value="year_asc">연도 오름차순 → 성명</option>
              <option value="year_desc">연도 내림차순 → 성명</option>
            </select>
          </label>
          <span style={{ fontSize: '0.82rem', color: '#64748b' }}>
            표 헤더를 눌러 다른 열 기준 정렬 시, 그 정렬이 우선됩니다.
          </span>
        </div>

        <div
          style={{
            padding: '15px',
            marginTop: '8px',
            marginBottom: '12px',
            backgroundColor: '#e8f5e9',
            border: '2px solid #43a047',
            borderRadius: '5px',
            fontSize: '14px',
            lineHeight: '1.6',
            fontWeight: '500',
          }}
        >
          <div style={{ color: '#2e7d32', fontWeight: 'bold', marginBottom: '8px', fontSize: '15px' }}>
            엑셀 업로드 안내
          </div>
          <div style={{ color: '#1b5e20' }}>
            • 업로드 순서: <strong>고용보험 → 건강보험 → 산재보험</strong> (각 파일은 해당 컬럼만 갱신)
            <br />
            • 매칭 기준: <strong>사업단 + 성명 + 주민번호/생년월일 앞 6자리</strong>
            <br />
            • 대상 명단: <strong>당해연도 + 전년도</strong> 참여자(종료자 포함), 중복 시 최신연도 우선
            <br />
            • 건강보험 엑셀: <strong>고지금액</strong> → 건강보험료 본인·기관부담금(동일 금액),{' '}
            <strong>요양고지보험료</strong> → 요양보험 본인·기관부담금(동일 금액)
            <br />
            <span style={{ color: '#c62828', fontWeight: '600' }}>
              연도/월은 필수이며, 사업단은 조회 필터로만 동작합니다.
            </span>
          </div>
        </div>

        <div style={{ ...filterGroup, marginTop: '8px', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>
          <label style={labelStyle}>
            엑셀 업로드
            <input
              ref={excelFileInputRef}
              type="file"
              accept=".xls,.xlsx"
              onChange={(e) => setExcelFile(e.target.files?.[0] || null)}
              style={{ marginLeft: '6px' }}
            />
          </label>
          <label style={labelStyle}>
            엑셀 비밀번호 (있는 경우만)
            <input
              type="password"
              value={excelPassword}
              onChange={(e) => setExcelPassword(e.target.value)}
              placeholder="비밀번호 없는 엑셀은 비워두세요"
              autoComplete="new-password"
              style={{
                marginLeft: '6px',
                padding: '4px 6px',
                border: '1px solid #d1d5db',
                borderRadius: 4,
              }}
            />
          </label>
          <button
            style={buttonStyle}
            onClick={handleExcelUpload}
            disabled={loading || importUploadKind}
          >
            고용보험 엑셀 불러오기
          </button>
          <button
            style={{ ...buttonStyle, background: '#0f766e' }}
            onClick={handleHealthExcelUpload}
            disabled={loading || importUploadKind}
          >
            건강보험 엑셀 불러오기
          </button>
          <button
            style={{ ...buttonStyle, background: '#7c3aed' }}
            onClick={handleIndustrialExcelUpload}
            disabled={loading || importUploadKind}
          >
            산재보험 엑셀 불러오기
          </button>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              type="button"
              style={{ ...secondaryButton, background: '#b91c1c' }}
              onClick={() => {
                if (month == null || month < 1 || month > 12) {
                  setFeedbackModal(MONTH_REQUIRED_MODAL);
                  return;
                }
                setResetModalPhase('warn');
              }}
              disabled={loading || importUploadKind || resetLoading}
            >
              입력 초기화
            </button>
          </div>
        </div>
      </div>

      {resultMessage && (
        <div
          style={{
            ...infoBox,
            borderColor: resultMessage.type === 'error' ? '#fca5a5' : '#93c5fd',
            background: resultMessage.type === 'error' ? '#fef2f2' : '#eff6ff',
            color: resultMessage.type === 'error' ? '#b91c1c' : '#1d4ed8',
          }}
        >
          {resultMessage.text}
        </div>
      )}

      {errors.length > 0 && (
        <div style={{ ...infoBox, borderColor: '#fca5a5', background: '#fef2f2', color: '#b91c1c' }}>
          <strong>처리되지 않은 항목</strong>
          <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
            {errors.map((item, idx) => (
              <li key={idx}>
                {item.row?.name || '성명 미상'} / {item.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div style={infoBox}>
        <div style={{ marginBottom: '10px', fontWeight: 600 }}>총 {listedParticipantCount}명</div>
        <div style={{ marginBottom: '6px', lineHeight: 1.55 }}>
          고용보험료 개인부담금 {eiInd.count}명 {formatNumber(eiInd.sum)}원, 기관부담금 {eiIns.count}명{' '}
          {formatNumber(eiIns.sum)}원
        </div>
        <div style={{ marginBottom: '6px', lineHeight: 1.55 }}>
          건강보험료 본인부담금 {hiInd.count}명 {formatNumber(hiInd.sum)}원, 기관부담금 {hiIns.count}명{' '}
          {formatNumber(hiIns.sum)}원
        </div>
        <div style={{ marginBottom: '6px', lineHeight: 1.55 }}>
          요양보험료 본인부담금 {ltcInd.count}명 {formatNumber(ltcInd.sum)}원, 기관부담금 {ltcIns.count}명{' '}
          {formatNumber(ltcIns.sum)}원
        </div>
        <div style={{ lineHeight: 1.55 }}>
          산재보험료 기관부담금 {indIns.count}명 {formatNumber(indIns.sum)}원
        </div>
      </div>

      <div style={tableContainer}>
        <table style={tableStyle}>
          <thead>
            <tr>
              {renderSortableTh('참여연도', SORT_KEYS.participantYear)}
              {renderSortableTh('성명', SORT_KEYS.name)}
              <th style={thStyle}>사업단</th>
              {renderSortableTh('고용보험료 개인부담금', SORT_KEYS.employmentIndividual, {
                background: '#e8f5e9',
              })}
              {renderSortableTh('고용보험료 기관부담금', SORT_KEYS.employmentInstitution, {
                background: '#e8f5e9',
              })}
              {renderSortableTh('건강보험료 본인부담금', SORT_KEYS.healthIndividual, {
                background: '#e8f5e9',
              })}
              {renderSortableTh('건강보험료 기관부담금', SORT_KEYS.healthInstitution, {
                background: '#e8f5e9',
              })}
              {renderSortableTh('요양보험료 본인부담금', SORT_KEYS.longTermCareIndividual, {
                background: '#e8f5e9',
              })}
              {renderSortableTh('요양보험료 기관부담금', SORT_KEYS.longTermCareInstitution, {
                background: '#e8f5e9',
              })}
              {renderSortableTh('산재보험료 기관부담금', SORT_KEYS.industrialInstitution, {
                background: '#e8f5e9',
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td style={tdStyle} colSpan={colCount}>
                  불러오는 중...
                </td>
              </tr>
            ) : month == null || month < 1 || month > 12 ? (
              <tr>
                <td style={tdStyle} colSpan={colCount}>
                  상단에서 월을 선택한 뒤 조회를 눌러 주세요.
                </td>
              </tr>
            ) : payrolls.length === 0 ? (
              <tr>
                <td style={tdStyle} colSpan={colCount}>
                  데이터가 없습니다.
                </td>
              </tr>
            ) : displayedPayrolls.length === 0 ? (
              <tr>
                <td style={tdStyle} colSpan={colCount}>
                  조회된 명단은 있으나, 고용·건강·요양·산재 보험료가 모두 0원인 경우는 표시하지 않습니다.
                </td>
              </tr>
            ) : (
              sortedPayrolls.map((item) => (
                <tr
                  key={item.id || `participant-${item.participantId}`}
                  style={
                    Number(item.participantYear) === year - 1
                      ? { backgroundColor: '#fff7ed' }
                      : undefined
                  }
                >
                  <td
                    style={{
                      ...tdStyle,
                      fontWeight: Number(item.participantYear) === year - 1 ? 700 : 500,
                      color: Number(item.participantYear) === year - 1 ? '#9a3412' : '#334155',
                    }}
                  >
                    {item.participantYear || '-'}
                  </td>
                  <td style={tdStyle}>{item.Participant?.name}</td>
                  <td style={tdStyle}>{projects.find((p) => Number(p.id) === Number(item.Participant?.projectId || item.projectId))?.projectName || '-'}</td>
                  <td style={{ ...tdStyle, background: '#f1f8e9' }}>
                    {formatNumber(item.employmentIndividual)}
                  </td>
                  <td style={{ ...tdStyle, background: '#f1f8e9' }}>
                    {formatNumber(item.employmentInstitution)}
                  </td>
                  <td style={{ ...tdStyle, background: '#f1f8e9' }}>
                    {formatNumber(item.healthIndividual)}
                  </td>
                  <td style={{ ...tdStyle, background: '#f1f8e9' }}>
                    {formatNumber(item.healthInstitution)}
                  </td>
                  <td style={{ ...tdStyle, background: '#f1f8e9' }}>
                    {formatNumber(item.longTermCareIndividual)}
                  </td>
                  <td style={{ ...tdStyle, background: '#f1f8e9' }}>
                    {formatNumber(item.longTermCareInstitution)}
                  </td>
                  <td style={{ ...tdStyle, background: '#f1f8e9' }}>
                    {formatNumber(item.industrialInstitution)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {(resetModalPhase === 'warn' || resetModalPhase === 'confirm') && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ei-reset-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => {
            if (!resetLoading) setResetModalPhase(null);
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px 28px',
              maxWidth: '480px',
              width: '100%',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {resetModalPhase === 'warn' ? (
              <>
                <div
                  id="ei-reset-title"
                  style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '14px', color: '#991b1b' }}
                >
                  입력 초기화 안내
                </div>
                <div style={{ fontSize: '0.95rem', color: '#334155', lineHeight: 1.65, marginBottom: '16px' }}>
                  <strong>
                    {year}년 {month}월
                    {selectedProjectName ? ` · ${selectedProjectName}` : ' · 전체 사업단'}
                  </strong>
                  조건으로 저장된 <strong>사회보험 입력 데이터(고용·건강·요양·산재)</strong>를 모두
                  삭제합니다.
                  <ul style={{ margin: '12px 0 0 18px', padding: 0 }}>
                    <li>엑셀로 반영한 보험료 금액이 전부 사라지며, 복구할 수 없습니다.</li>
                    <li>참여자 명단 자체는 삭제되지 않습니다.</li>
                    <li>사업단을 선택한 상태에서는 해당 사업단에 속한 행만 삭제됩니다.</li>
                  </ul>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    style={secondaryButton}
                    onClick={() => setResetModalPhase(null)}
                    disabled={resetLoading}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    style={{ ...buttonStyle, background: '#b91c1c' }}
                    onClick={() => setResetModalPhase('confirm')}
                    disabled={resetLoading}
                  >
                    다음 (최종 확인)
                  </button>
                </div>
              </>
            ) : (
              <>
                <div
                  id="ei-reset-title"
                  style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '14px', color: '#991b1b' }}
                >
                  최종 확인
                </div>
                <div style={{ fontSize: '0.95rem', color: '#334155', lineHeight: 1.65, marginBottom: '20px' }}>
                  정말 <strong>삭제(초기화)</strong>하시겠습니까? 이 작업은 되돌릴 수 없습니다.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    style={secondaryButton}
                    onClick={() => setResetModalPhase(null)}
                    disabled={resetLoading}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    style={secondaryButton}
                    onClick={() => setResetModalPhase('warn')}
                    disabled={resetLoading}
                  >
                    이전
                  </button>
                  <button
                    type="button"
                    style={{
                      ...buttonStyle,
                      background: '#7f1d1d',
                      opacity: resetLoading ? 0.7 : 1,
                    }}
                    onClick={handleResetInsuranceExecute}
                    disabled={resetLoading}
                  >
                    {resetLoading ? '처리 중…' : '삭제 실행'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {importUploadKind && (
        <div
          role="dialog"
          aria-modal="true"
          aria-busy="true"
          aria-label={IMPORT_UPLOAD_COPY[importUploadKind].ariaLabel}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.5)',
            zIndex: 10000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '28px 32px',
              maxWidth: '400px',
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>
              {IMPORT_UPLOAD_COPY[importUploadKind].title}
            </div>
            <div style={{ fontSize: '0.95rem', color: '#475569', lineHeight: 1.55 }}>
              {IMPORT_UPLOAD_COPY[importUploadKind].body}
              <br />
              창을 닫지 말고 잠시만 기다려 주세요.
            </div>
            <div style={{ marginTop: '20px', fontSize: '0.85rem', color: '#94a3b8' }}>처리 중…</div>
          </div>
        </div>
      )}

      {feedbackModal && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ei-feedback-title"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(15, 23, 42, 0.45)',
            zIndex: 10001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '16px',
          }}
          onClick={() => setFeedbackModal(null)}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '12px',
              padding: '24px 28px',
              maxWidth: '560px',
              width: '100%',
              maxHeight: '80vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              id="ei-feedback-title"
              style={{
                fontSize: '1.05rem',
                fontWeight: 700,
                marginBottom: '12px',
                color:
                  feedbackModal.type === 'error'
                    ? '#b91c1c'
                    : feedbackModal.type === 'info'
                      ? '#0f172a'
                      : '#1d4ed8',
              }}
            >
              {feedbackModal.title ||
                (feedbackModal.type === 'success'
                  ? '반영 완료'
                  : feedbackModal.type === 'info'
                    ? '안내'
                    : '알림')}
            </div>
            <div
              style={{
                fontSize: '0.95rem',
                color: '#334155',
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowY: 'auto',
                flex: '1 1 auto',
              }}
            >
              {feedbackModal.message}
            </div>
            <div style={{ marginTop: '20px', textAlign: 'right' }}>
              <button type="button" style={buttonStyle} onClick={() => setFeedbackModal(null)}>
                확인
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EmploymentInsurancePage;
